import styles from './index.module.css'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import PopoverContent from 'components/ui/popover2/PopoverContent'
type Props = {
  activator: React.ReactNode
  content: React.ReactNode
  // Optional preferred position
  position?: 'bottom' | 'left' | 'right' | 'top'
  contentClassName?: string
  pointer?: 'default' | 'none'
  popoverClassname?: string
  delay?: number
  // 'click' toggles on trigger click and closes on outside click instead of hover
  trigger?: 'hover' | 'click'
  // see FlipStrategy in PopoverContent
  flipStrategy?: 'fit' | 'space'
}

/**
 * @deprecated Use `HoverCard` from `components/ds` instead.
 * @see {@link components/ds/HoverCard}
 * @see https://www.radix-ui.com/themes/docs/components/hover-card
 * @migration
 * - Replace with HoverCard compound component from components/ds:
 *   ```tsx
 *   // Legacy
 *   <HoverCard
 *     activator={<Button>Hover me</Button>}
 *     content={<div>Card content</div>}
 *     position="right"
 *     delay={200}
 *   />
 *
 *   // DS alternative
 *   import { HoverCard, Flex, Text } from 'components/ds'
 *
 *   <HoverCard.Root openDelay={200}>
 *     <HoverCard.Trigger>
 *       <Button>Hover me</Button>
 *     </HoverCard.Trigger>
 *     <HoverCard.Content side="right">
 *       <Flex direction="column" gap="2">
 *         <Text>Card content</Text>
 *       </Flex>
 *     </HoverCard.Content>
 *   </HoverCard.Root>
 *   ```
 * - Prop mapping:
 *   - activator → Wrap in `<HoverCard.Trigger>`
 *   - content → Place in `<HoverCard.Content>`
 *   - position → side prop on HoverCard.Content ("top" | "right" | "bottom" | "left")
 *   - delay → openDelay on HoverCard.Root (in milliseconds)
 *   - contentClassName → className on HoverCard.Content
 *   - pointer/popoverClassname → Not directly supported, use Radix styling
 *
 * A hover card component that displays content in a popover when the activator element is hovered.
 * The card appears after a configurable delay and automatically hides when the user scrolls or moves the mouse away.
 *
 * @param activator - The element that triggers the hover card when hovered
 * @param content - The content to display inside the hover card
 * @param position - The preferred position of the popover relative to the activator (default: 'right')
 * @param contentClassName - Optional CSS class name for the content container
 * @param pointer - Whether to show a pointer/arrow on the popover (default: 'default')
 * @param popoverClassname - Optional CSS class name for the popover container
 * @param delay - The delay in milliseconds before showing the hover card (default: 200)
 * @returns The activator element wrapped with hover functionality, or just the activator if no content is provided
 */
const HoverCard = ({
  activator,
  content,
  position = 'right',
  contentClassName,
  pointer = 'default',
  popoverClassname,
  delay = 200,
  trigger = 'hover',
  flipStrategy = 'fit',
}: Props): React.ReactNode | React.ReactElement<'div'> => {
  const containerRef = useRef<HTMLElement | null>(null)
  const targetRef = useRef<HTMLDivElement | null>(null)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    containerRef.current =
      document.getElementById('app') || document.getElementById('__next')
  }, [])
  const [isActive, setIsActive] = useState<boolean>(false)
  const reset = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current)
  }, [])
  // Hide the popover when the user scrolls anywhere on the page
  useEffect(() => {
    const handleGlobalScroll = () => {
      setIsActive(false)
      reset()
    }

    // Catch the scroll event on the capturing phase
    window.addEventListener('scroll', handleGlobalScroll, true)
    return () => {
      window.removeEventListener('scroll', handleGlobalScroll, true)
      reset()
    }
  }, [reset])
  const delayedSetActive = useCallback(
    (active: boolean) => {
      reset()
      timeout.current = setTimeout(() => {
        setIsActive(active)
      }, delay)
    },
    [delay, reset],
  )
  const close = useCallback(() => {
    reset()
    setIsActive(false)
  }, [reset])

  const renderPortal = (container: HTMLElement) => {
    return ReactDOM.createPortal(
      <PopoverContent
        position={position}
        targetRef={targetRef}
        onClose={trigger === 'click' ? close : reset}
        pointer={pointer}
        popoverClassname={popoverClassname}
        flipStrategy={flipStrategy}
      >
        <div className={contentClassName ?? styles.content}>{content}</div>
      </PopoverContent>,
      container,
    )
  }

  if (!content) {
    return activator
  }

  const triggerProps =
    trigger === 'click'
      ? { onClick: () => setIsActive(active => !active) }
      : {
          onMouseLeave: () => delayedSetActive(false),
          onMouseOver: () => delayedSetActive(true),
          onClick: () => setIsActive(true),
        }

  return (
    <div ref={targetRef} {...triggerProps}>
      {activator}
      {isActive && containerRef.current && renderPortal(containerRef.current)}
    </div>
  )
}

export default HoverCard
